import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProfiles, useSaveMaterial } from '@/hooks/useInventoryMaster'
import type { CreateMaterialPayload } from '@/api/inventoryApi'
import { PROFILE_TYPE_META } from '@/lib/inventoryProfiles'
import { CREATABLE_MATERIAL_TYPES, type MaterialStatus } from '@/types/inventory'

const STATUSES: MaterialStatus[] = [
  'available',
  'reserved',
  'in_transit',
  'quarantine',
  'hold',
  'retired',
]


interface NewMaterialModalProps {
  open: boolean
  onClose: () => void
}

/** Create a material: identity fields + pick a profile per category. */
export function NewMaterialModal({ open, onClose }: NewMaterialModalProps) {
  const { data: allProfiles = [] } = useProfiles() // no type → every category's profiles
  const saveMaterial = useSaveMaterial()

  const [materialCode, setMaterialCode] = useState('')
  const [materialName, setMaterialName] = useState('')
  // Type and status both start unset so the trigger shows its placeholder and
  // the user makes a deliberate choice, rather than silently accepting whatever
  // happened to be preselected.
  const [materialType, setMaterialType] = useState('')
  const [status, setStatus] = useState<MaterialStatus | ''>('')
  const [primaryProductCode, setPrimaryProductCode] = useState('')
  const [productCodes, setProductCodes] = useState('')
  const [picks, setPicks] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setMaterialCode('')
      setMaterialName('')
      setMaterialType('')
      setStatus('')
      setPrimaryProductCode('')
      setProductCodes('')
      setPicks({})
      setError(null)
    }
  }, [open])

  const profilesByType = useMemo(() => {
    const m = new Map<string, typeof allProfiles>()
    for (const p of allProfiles) {
      const list = m.get(p.profileType) ?? []
      list.push(p)
      m.set(p.profileType, list)
    }
    return m
  }, [allProfiles])

  function save() {
    if (!materialCode.trim() || !materialName.trim() || !materialType.trim()) {
      setError('Code, Name and Type are required.')
      return
    }
    // No materialId: the server mints the MAT-###### key, the same way it mints
    // lot numbers. Typing it by hand only ever produced duplicates and typos.
    const payload: CreateMaterialPayload = {
      materialCode: materialCode.trim(),
      materialName: materialName.trim(),
      materialType: materialType.trim(),
    }
    // Omitted when unset — the backend defaults a new material to `available`.
    if (status) payload.status = status
    // Product codes belong to this material, not to a shared profile. The list
    // is entered as free text and split on commas; blanks are dropped.
    if (primaryProductCode.trim()) payload.primaryProductCode = primaryProductCode.trim()
    const codes = productCodes
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)
    if (codes.length > 0) payload.productCodes = codes
    const rec = payload as unknown as Record<string, unknown>
    for (const meta of PROFILE_TYPE_META) {
      if (picks[meta.type]) rec[meta.fkField] = picks[meta.type]
    }
    saveMaterial.mutate(
      { payload },
      {
        onSuccess: () => onClose(),
        onError: (e) => setError(e instanceof Error ? e.message : 'Failed to create material.'),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='text-lg'>New material</DialogTitle>
        </DialogHeader>

        <p className='text-xs text-muted-foreground'>
          The Material ID (MAT-…) is assigned when you save.
        </p>

        <div className='grid grid-cols-2 gap-3'>
          <Input value={materialCode} onChange={(e) => setMaterialCode(e.target.value)} placeholder='Material Code' className='col-span-2 h-9' />
          <Input value={materialName} onChange={(e) => setMaterialName(e.target.value)} placeholder='Material Name' className='col-span-2 h-9' />
          <Input
            value={primaryProductCode}
            onChange={(e) => setPrimaryProductCode(e.target.value)}
            placeholder='Primary Product Code'
            className='h-9'
          />
          <Input
            value={productCodes}
            onChange={(e) => setProductCodes(e.target.value)}
            placeholder='Other product codes (comma-separated)'
            className='h-9'
          />
          <Select value={materialType} onValueChange={setMaterialType}>
            <SelectTrigger className='h-9'>
              <SelectValue placeholder='Material type' />
            </SelectTrigger>
            <SelectContent>
              {CREATABLE_MATERIAL_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => setStatus(v as MaterialStatus)}>
            <SelectTrigger className='h-9'>
              <SelectValue placeholder='Status' />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='max-h-[38vh] space-y-2 overflow-y-auto rounded-xl border border-gray-200 p-3'>
          <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>Profiles</p>
          {PROFILE_TYPE_META.map((meta) => {
            const opts = profilesByType.get(meta.type) ?? []
            return (
              <div key={meta.type} className='flex items-center justify-between gap-3'>
                <span className='text-sm text-gray-600'>{meta.label}</span>
                <Select value={picks[meta.type] ?? ''} onValueChange={(v) => setPicks((p) => ({ ...p, [meta.type]: v }))}>
                  <SelectTrigger className='h-8 w-52'>
                    <SelectValue placeholder='— none —' />
                  </SelectTrigger>
                  <SelectContent>
                    {opts.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )
          })}
          <p className='pt-1 text-xs text-gray-400'>
            Need a different profile? Create it in Master Data Config, then pick it here.
          </p>
        </div>

        {error && <p className='text-sm text-red-600'>{error}</p>}

        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saveMaterial.isPending}>
            {saveMaterial.isPending ? 'Creating…' : 'Create material'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
